import { useCallback, useRef } from "react"
import { useTranslation } from "react-i18next"
import type { LngLatBoundsLike } from "maplibre-gl"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import LightMapView, {
  LightMapViewHandle,
} from "@/components/maps/LightMapView"
import AgentMapLayers from "@/components/slickAI/AgentMapLayers"
import AgentMapControls from "@/components/slickAI/AgentMapControls"
import { AgentMapNav } from "@/components/slickAI/AgentMapPanel"
import type { MapViewData } from "@/lib/aiWidgetMapParser"
import type { MapStyle } from "@/components/maps/MapManager"

type AgentMapFullscreenDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  chatJid: string
  mapData: MapViewData
  count: number
  index: number
  bounds: LngLatBoundsLike | null
  mapStyle: MapStyle
  onChangeMapStyle: (style: MapStyle) => void
}

const AgentMapFullscreenDialog = ({
  open,
  onOpenChange,
  chatJid,
  mapData,
  count,
  index,
  bounds,
  mapStyle,
  onChangeMapStyle,
}: AgentMapFullscreenDialogProps) => {
  const { t } = useTranslation()
  const mapHandleRef = useRef<LightMapViewHandle>(null)
  const fitAll = useCallback(() => {
    if (bounds && mapHandleRef.current) {
      mapHandleRef.current.fitBounds(bounds)
    }
  }, [bounds])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[90vh] max-w-[95vw] flex-col gap-0 p-0"
        onInteractOutside={(event) => {
          // The style picker's PopoverContent is portal'd to body, so a
          // click on its items counts as "outside the dialog" and Radix
          // closes the dialog by default. Ignore interactions whose
          // target lives in a Radix Popper wrapper (popover / dropdown).
          const target = event.target as HTMLElement | null
          if (target?.closest("[data-radix-popper-content-wrapper]")) {
            event.preventDefault()
          }
        }}
      >
        <DialogTitle className="sr-only">{t("ai.map.agentMap")}</DialogTitle>
        <div className="relative flex-1 overflow-hidden">
          <LightMapView
            ref={mapHandleRef}
            fitBoundsTo={bounds}
            mapStyle={mapStyle}
            className="absolute inset-0"
          >
            <AgentMapLayers mapData={mapData} generation={index} />
          </LightMapView>
          <AgentMapControls
            currentStyle={mapStyle}
            onChangeStyle={onChangeMapStyle}
            onFitAll={fitAll}
            canFitAll={!!bounds}
            onToggleFullscreen={() => onOpenChange(false)}
            isFullscreen
            popoverContentClassName="z-[1020]"
          />
        </div>
        {count > 1 && (
          <AgentMapNav chatJid={chatJid} count={count} index={index} />
        )}
      </DialogContent>
    </Dialog>
  )
}

export default AgentMapFullscreenDialog
